// frontend/src/services/userOnboardingService.ts
/**
 * Serviço para onboarding automático do usuário
 * 🧠 Pensado por Alcélio Gomes - Codificado com Claude Code
 * 
 * Cria estrutura padrão (projeto + agente + webhook inativo) no primeiro login
 */

import { agentService } from './agentService';
import { WebhookService } from './WwebhookService';
import { projectService } from './projectService';
import { IWebhookDocument } from '../types/webhook';
import { Agent, Project } from '../types';

interface UserSetupResult {
  project: Project;
  agent: Agent;
  webhook: IWebhookDocument;
  isFirstTime: boolean;
}

/**
 * Template do agente passivo pré-configurado
 */
const PRE_CONFIGURED_AGENT_TEMPLATE = {
  nome: "Monitor Universal",
  descricao: "Agente passivo pré-configurado. Ative seu webhook para começar a receber mensagens.",
  
  modo_operacao: "seletivo" as const,
  status: 'inativo' as const, // Inativo até webhook ser ativado
  
  configuracao: {
    modo_operacao: "seletivo" as const,
    configuracoes_seletivas: {
      palavras_chave: [], // Captura tudo
      intencoes: [],
      tempo_inatividade: undefined
    }
  },
  
  tipo_agente: "webhook_passivo",
  auto_created: true,
  webhook_ready: true,
  pre_configured: true
};

/**
 * Template do webhook pré-criado (inativo)
 */
const PRE_CONFIGURED_WEBHOOK_TEMPLATE = {
  nome: "Webhook Principal",
  url: "", // Vazio - usuário preencherá
  ativo: false, // Inativo até usuário configurar
  filtrosInscricao: {}, // Campo obrigatório
  
  configuracao: { // Campo singular, não plural
    pre_created: true,
    awaiting_activation: true,
    modo_passivo: true
  }
};

class UserOnboardingService {
  private webhookService: WebhookService;

  constructor() {
    this.webhookService = WebhookService.getInstance();
  }

  /**
   * Verifica se usuário já tem estrutura básica configurada (agente + webhook)
   */
  async checkUserSetup(userId: string): Promise<{ hasSetup: boolean; needsActivation: boolean }> {
    try {
      // Verificar se já tem projeto
      const projects = await projectService.getProjects();
      
      if (projects.length === 0) {
        return { hasSetup: false, needsActivation: false };
      }

      const defaultProject = projects[0];
      
      // Verificar se tem qualquer agente (mais seguro que verificar tipos específicos)
      const agents = await agentService.getAgents(defaultProject.id);
      const hasAnyAgent = agents.length > 0;
      
      // Verificar se tem webhook (ativo ou inativo)
      const webhooks = await this.webhookService.buscarPorProjeto(defaultProject.id);
      const hasWebhook = webhooks.length > 0;
      const inactiveWebhook = webhooks.find(w => !w.ativo && (
        w.configuracao?.pre_created || 
        w.configuracoes?.pre_created
      ));
      
      // Se tem agentes E webhooks, considera setup completo
      const hasSetup = hasAnyAgent && hasWebhook;
      
      console.log('🔍 Setup Check:', {
        projectId: defaultProject.id,
        agentCount: agents.length,
        webhookCount: webhooks.length,
        hasSetup,
        agentNames: agents.map(a => a.nome).join(', ')
      });
      
      return {
        hasSetup,
        needsActivation: !!inactiveWebhook
      };

    } catch (error) {
      console.error('Erro ao verificar setup do usuário:', error);
      return { hasSetup: false, needsActivation: false };
    }
  }

  /**
   * Cria estrutura padrão para usuário (agente + webhook para projeto existente)
   */
  async createUserDefaultSetup(userId: string, projectName?: string): Promise<UserSetupResult> {
    try {
      // 1. Verificar se já tem projeto (backend cria automaticamente no registro)
      // FORÇAR REFRESH para novo usuário evitar cache antigo
      let projects = await projectService.getProjects(true); // true = forceRefresh
      let project;
      
      if (projects.length === 0) {
        // Só criar projeto se não existir nenhum (caso de usuários antigos)
        console.log('Nenhum projeto encontrado, criando projeto padrão');
        project = await this.createDefaultProject(projectName || 'Meu Projeto');
      } else {
        // Usar projeto existente (criado pelo backend)
        project = projects[0];
        console.log('Usando projeto existente:', project.nome);
      }
      
      // 2. Verificar se já tem qualquer agente
      const existingAgents = await agentService.getAgents(project.id);
      
      console.log('🤖 Agents Check:', {
        projectId: project.id,
        existingCount: existingAgents.length,
        agentNames: existingAgents.map(a => `${a.nome} (${a.tipo_agente})`).join(', ')
      });
      
      let agent;
      if (existingAgents.length === 0) {
        // Criar agente passivo pré-configurado
        const agentData = {
          ...PRE_CONFIGURED_AGENT_TEMPLATE,
          projetoId: project.id,
          plataformaId: 4 // Webhook platform
        };
        
        agent = await agentService.createAgent(agentData);
        console.log('✅ Agente criado:', agent.nome);
      } else {
        agent = existingAgents[0]; // Usar primeiro agente existente
        console.log('✅ Agente já existe:', agent.nome, '- pulando criação');
      }
      
      // 3. Verificar se já tem webhook (com tratamento de erro 404)
      let existingWebhooks: IWebhookDocument[] = [];
      try {
        existingWebhooks = await this.webhookService.buscarPorProjeto(project.id);
      } catch (error) {
        console.log('📝 Erro ao buscar webhooks (projeto novo), continuando:', error);
        existingWebhooks = [];
      }
      
      // Buscar webhook pré-criado pelo backend (aceitar ambas nomenclaturas)
      const preCreatedWebhook = existingWebhooks.find(w => 
        w.nome === "Webhook Principal" && (
          w.configuracao?.pre_created || 
          w.configuracao?.auto_generated ||
          w.configuracoes?.pre_created || 
          w.configuracoes?.auto_generated
        )
      );
      
      let webhook;
      if (preCreatedWebhook) {
        webhook = preCreatedWebhook;
        console.log('✅ Webhook pré-criado encontrado:', webhook.id);
      } else {
        console.log('⚠️ Webhook pré-criado não encontrado. Backend deveria ter criado durante registro.');
        // NÃO criar novo - retornar null para indicar problema
        webhook = null;
      }
      
      // 4. Salvar no localStorage que setup foi verificado
      localStorage.setItem('user_setup_verified', 'true');
      
      return {
        project,
        agent,
        webhook: webhook as IWebhookDocument,
        isFirstTime: existingAgents.length === 0 && !webhook
      };

    } catch (error) {
      console.error('Erro ao criar/verificar setup padrão:', error);
      throw new Error('Falha ao configurar estrutura de webhook. Tente novamente.');
    }
  }

  /**
   * Ativa webhook existente com URL fornecida
   */
  async activateWebhook(webhookId: string, url: string, nome?: string): Promise<IWebhookDocument> {
    try {
      // Validar URL
      const urlValidation = this.validateWebhookUrl(url);
      if (!urlValidation.valid) {
        throw new Error(urlValidation.error);
      }

      // Atualizar webhook
      const updateData = {
        url: url,
        ativo: true,
        nome: nome || "Webhook Ativado",
        configuracoes: {
          activated_at: new Date().toISOString(),
          modo_passivo: true,
          pre_created: false // Não é mais pré-criado
        }
      };

      const webhook = await this.webhookService.atualizar(webhookId, updateData);

      // Ativar agente vinculado também
      if (webhook.agenteId) {
        await agentService.updateAgent(webhook.agenteId, {
          status: 'ativo',
          descricao: "Agente passivo ativo - encaminhando mensagens via webhook"
        });
      }

      return webhook;

    } catch (error) {
      console.error('Erro ao ativar webhook:', error);
      throw new Error('Falha ao ativar webhook. Verifique a URL e tente novamente.');
    }
  }

  /**
   * Busca webhook inativo do usuário
   */
  async getUserInactiveWebhook(projectId: string): Promise<IWebhookDocument | null> {
    try {
      const webhooks = await this.webhookService.buscarPorProjeto(projectId);
      return webhooks.find(w => !w.ativo && (
        w.configuracao?.pre_created || 
        w.configuracao?.auto_generated ||
        w.configuracoes?.pre_created || 
        w.configuracoes?.auto_generated
      )) || null;
    } catch (error) {
      console.error('Erro ao buscar webhook inativo:', error);
      return null;
    }
  }

  /**
   * Cria projeto padrão
   */
  private async createDefaultProject(projectName: string) {
    const projectData = {
      nome: projectName,
      descricao: 'Projeto criado durante o registro para integração webhook',
      slug: this.generateSlug(projectName),
      tipoPlano: 'basico',
      ativo: true, // ✅ CORRIGIDO: campo obrigatório
      configuracoes: {
        auto_created: true,
        webhook_ready: true,
        first_time_setup: true
      }
    };

    return await projectService.createProject(projectData);
  }


  /**
   * Gera slug para projeto
   */
  private generateSlug(nome: string): string {
    return nome
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 30);
  }

  /**
   * Valida URL do webhook
   */
  private validateWebhookUrl(url: string): { valid: boolean; error?: string } {
    try {
      const urlObj = new URL(url);
      
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return { valid: false, error: 'URL deve usar HTTP ou HTTPS' };
      }
      
      return { valid: true };
    } catch {
      return { valid: false, error: 'URL inválida' };
    }
  }
}

export const userOnboardingService = new UserOnboardingService();
export default userOnboardingService;